import { CheckCircleOutlined, ExclamationCircleOutlined, LogoutOutlined, ThunderboltOutlined } from "@ant-design/icons";
import { Alert, Button, Card, Col, Modal, Row, Spin, Tag, Typography } from "antd";
import { InstallationModeContext } from "contexts/InstallationMode/installation-mode-context";
import moment from "moment";
import { useSmartgrdEmonPowerStatusSubscription } from "pacts/app-webcore/hasura-webcore.graphql";
import React, { useContext, useEffect, useState } from "react";
import { PositionFunction } from "types/InstallationMode.d";

const { Title, Text } = Typography;

interface MeterInstallationModeModalProps {
  visible: boolean;
  onClose: () => void;
  meterPositionId: string;
  // meterName: string; // Unused prop - commented out
  nodeStatusInfo?: {
    nodeStatus?: string;
    nodeStatusInInstallationMode?: string;
    lastTransmission?: string;
    nodeMacId?: string;
  };
}

interface MeterLiveData {
  lastUpdate?: string;
  channelData?: Array<{
    channel: number;
    measurements: Array<{
      type: string;
      value: number;
      lastUpdated: string;
    }>;
    nodeMacId: string;
    meterType: string;
  }>;
}

const MeterInstallationModeModal: React.FC<MeterInstallationModeModalProps> = ({
  visible,
  onClose,
  meterPositionId,
  // meterName, // Unused variable - commented out
  nodeStatusInfo,
}) => {
  const { handleExitKey, currentKeyEntryOfInstaller, isLoading /* keyName */ } = useContext(InstallationModeContext);

  const [liveData, setLiveData] = useState<MeterLiveData>({});
  const [lastRefresh, setLastRefresh] = useState<string>(moment().format("HH:mm:ss"));

  // Use the real subscription for live meter data
  const {
    data,
    loading: dataLoading,
    error,
  } = useSmartgrdEmonPowerStatusSubscription({
    variables: { meteringPointPositionId: meterPositionId },
    skip: !meterPositionId || !visible,
  });

  // Process the real-time data from the subscription
  useEffect(() => {
    if (data?.smartgrdEmonPowerStatus) {
      const measurements = data.smartgrdEmonPowerStatus;

      if (measurements.length > 0) {
        // Get the most recent update time
        const latestUpdate = measurements
          .map((m) => m.mappedTime)
          .filter(Boolean)
          .sort()
          .pop();

        // Group measurements by channel to show all measurement types per channel
        const groupedByChannel = measurements.reduce((acc, measurement) => {
          const channelIndex = (measurement.channelIndex ?? 0) + 1; // Convert 0-based to 1-based

          if (!acc[channelIndex]) {
            acc[channelIndex] = {
              channel: channelIndex,
              measurements: [],
              nodeMacId: measurement.nodeMacId || "",
              meterType: measurement.meterType || "CT",
            };
          }

          acc[channelIndex].measurements.push({
            type: measurement.measurementType || "energy",
            value: measurement.lastPowerReading || 0,
            lastUpdated: measurement.mappedTime || "",
          });

          return acc;
        }, {} as Record<number, any>);

        const channelData = Object.values(groupedByChannel);

        setLiveData({
          lastUpdate: latestUpdate || moment().format("YYYY-MM-DD HH:mm:ss"),
          channelData,
        });

        setLastRefresh(moment().format("HH:mm:ss"));
      } else {
        // No measurements available
        setLiveData({
          lastUpdate: undefined,
          channelData: [],
        });
        setLastRefresh(moment().format("HH:mm:ss"));
      }
    }
  }, [data]);

  const handleExit = async () => {
    await handleExitKey(onClose);
  };

  const isCurrentMeterInstallation =
    currentKeyEntryOfInstaller?.keyPositionId === meterPositionId &&
    currentKeyEntryOfInstaller?.positionFunction === PositionFunction.METERING_POINT;

  // Show connection status based on data availability
  const isOnline = !!(liveData.channelData && liveData.channelData.length > 0 && liveData.lastUpdate);

  const getStatusIcon = () => {
    if (isOnline) {
      return <CheckCircleOutlined style={{ color: "#52c41a", fontSize: "16px" }} />;
    }
    return <ExclamationCircleOutlined style={{ color: "#faad14", fontSize: "16px" }} />;
  };

  const getMeasurementUnit = (type: string): string => {
    if (type === "power") {
      return "W";
    }
    if (type === "consumed_energy" || type === "energy") {
      return "kWh";
    }
    return "";
  };

  // const getNetworkStatusColor = (status?: string) => { // Unused function - commented out
  //   switch (status?.toUpperCase()) {
  //     case "ONLINE":
  //       return "#52c41a";
  //     case "OFFLINE":
  //       return "#ff4d4f";
  //     default:
  //       return "#fa8c16";
  //   }
  // };

  return (
    <Modal
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <ThunderboltOutlined style={{ color: "#1890ff", fontSize: "20px" }} />
          <div>
            <span style={{ fontSize: "18px", fontWeight: 600 }}>CT Meter Installation Mode</span>
            <div style={{ fontSize: "12px", color: "#666", fontWeight: 400, marginTop: 2 }}>
              {getStatusIcon()} Live monitoring active • Last updated: {lastRefresh}
            </div>
          </div>
        </div>
      }
      open={visible}
      onCancel={onClose}
      width={1000}
      bodyStyle={{ padding: "24px" }}
      footer={
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Text type="secondary" style={{ fontSize: "12px" }}>
            Monitor readings and verify installation before exiting
          </Text>
          {isCurrentMeterInstallation && (
            <Button
              type="primary"
              danger
              size="large"
              icon={<LogoutOutlined />}
              onClick={handleExit}
              loading={isLoading}
            >
              Exit Installation Mode
            </Button>
          )}
        </div>
      }
    >
      <div>
        {error && (
          <Alert
            message="Error Loading Measurements"
            description={error.message}
            type="error"
            style={{ marginBottom: 24, borderRadius: "8px" }}
          />
        )}

        {/* Node Status Information */}
        {nodeStatusInfo && (
          <Card
            title="Device Information"
            style={{ marginBottom: 24, borderRadius: "8px" }}
            bodyStyle={{ padding: "16px 24px" }}
          >
            <Row gutter={[24, 16]}>
              <Col xs={24} md={12}>
                <div style={{ marginBottom: 12 }}>
                  <Text type="secondary" style={{ fontSize: "12px", display: "block", marginBottom: 4 }}>
                    NODE MAC ID
                  </Text>
                  <Text strong style={{ fontFamily: "monospace", fontSize: "14px" }}>
                    {nodeStatusInfo.nodeMacId || "Unknown"}
                  </Text>
                </div>
              </Col>

              <Col xs={24} md={12}>
                <div style={{ marginBottom: 12 }}>
                  <Text type="secondary" style={{ fontSize: "12px", display: "block", marginBottom: 4 }}>
                    NETWORK STATUS
                  </Text>
                  <Tag
                    color={nodeStatusInfo.nodeStatus === "ONLINE" ? "green" : "red"}
                    style={{ fontSize: "13px", fontWeight: 500 }}
                  >
                    {nodeStatusInfo.nodeStatus || "Unknown"}
                  </Tag>
                </div>
              </Col>
            </Row>
          </Card>
        )}

        <Spin spinning={dataLoading}>
          {/* Channel Details */}
          {liveData.channelData && liveData.channelData.length > 0 ? (
            <Card
              title="Individual Channel Breakdown"
              style={{ marginBottom: 24, borderRadius: "8px" }}
              bodyStyle={{ padding: "16px 24px" }}
            >
              <Row gutter={[16, 16]}>
                {liveData.channelData.map((channel) => (
                  <Col xs={24} sm={12} lg={8} key={channel.channel}>
                    <Card
                      size="small"
                      style={{
                        backgroundColor: "#fafafa",
                        border: "1px solid #e8e8e8",
                        borderRadius: "6px",
                      }}
                      bodyStyle={{ padding: "16px 12px" }}
                    >
                      <div style={{ marginBottom: 12, textAlign: "center" }}>
                        <Text type="secondary" style={{ fontSize: "12px", fontWeight: 600 }}>
                          CHANNEL {channel.channel}
                        </Text>
                      </div>

                      {/* Display all measurement types for this channel */}
                      {channel.measurements.map((measurement, index) => (
                        <div
                          key={measurement.type}
                          style={{ marginBottom: index < channel.measurements.length - 1 ? 12 : 8 }}
                        >
                          <div style={{ fontSize: "18px", fontWeight: 600, color: "#3f8600", textAlign: "center" }}>
                            {measurement.value.toFixed(4)}{" "}
                            <span style={{ fontSize: "14px", color: "#666" }}>
                              {getMeasurementUnit(measurement.type)}
                            </span>
                          </div>
                          <div style={{ fontSize: "10px", color: "#999", textAlign: "center", marginTop: 2 }}>
                            {measurement.type.replace("_", " ").toUpperCase()}
                          </div>
                        </div>
                      ))}

                      <div style={{ fontSize: "11px", color: "#999", marginTop: 8, textAlign: "center" }}>
                        {channel.meterType}
                      </div>
                      {channel.nodeMacId && (
                        <div
                          style={{
                            fontSize: "10px",
                            color: "#bbb",
                            fontFamily: "monospace",
                            wordBreak: "break-all",
                            textAlign: "center",
                            marginTop: 4,
                          }}
                        >
                          {channel.nodeMacId}
                        </div>
                      )}
                    </Card>
                  </Col>
                ))}
              </Row>
            </Card>
          ) : (
            <Card
              style={{ marginBottom: 24, borderRadius: "8px", textAlign: "center" }}
              bodyStyle={{ padding: "40px 24px" }}
            >
              <div style={{ color: "#bbb", fontSize: "48px", marginBottom: 16 }}>
                <ThunderboltOutlined />
              </div>
              <Title level={4} style={{ color: "#999", marginBottom: 8 }}>
                No Measurements Detected
              </Title>
              <Text type="secondary">
                Waiting for channel data to appear. Ensure CT clamps are properly connected and the node has power.
              </Text>
            </Card>
          )}
        </Spin>

        {/* Installation Instructions */}
        <Card title="Installation Checklist" style={{ borderRadius: "8px" }} bodyStyle={{ padding: "20px 24px" }}>
          <div style={{ lineHeight: 1.8 }}>
            <div style={{ marginBottom: 12 }}>
              <Text strong style={{ color: "#1890ff" }}>
                1.
              </Text>
              <span style={{ marginLeft: 8 }}>
                Verify that the CT clamps are properly installed around the conductors
              </span>
            </div>
            <div style={{ marginBottom: 12 }}>
              <Text strong style={{ color: "#1890ff" }}>
                2.
              </Text>
              <span style={{ marginLeft: 8 }}>
                Check that power readings are within expected range for each channel
              </span>
            </div>
            <div style={{ marginBottom: 12 }}>
              <Text strong style={{ color: "#1890ff" }}>
                3.
              </Text>
              <span style={{ marginLeft: 8 }}>Ensure the connection status shows &quot;Online&quot;</span>
            </div>
            <div style={{ marginBottom: 12 }}>
              <Text strong style={{ color: "#1890ff" }}>
                4.
              </Text>
              <span style={{ marginLeft: 8 }}>Monitor each channel for consistent power readings</span>
            </div>
            <div style={{ marginBottom: 12 }}>
              <Text strong style={{ color: "#1890ff" }}>
                5.
              </Text>
              <span style={{ marginLeft: 8 }}>Verify that the node MAC ID matches your physical device</span>
            </div>
            <div>
              <Text strong style={{ color: "#1890ff" }}>
                6.
              </Text>
              <span style={{ marginLeft: 8 }}>
                Exit installation mode when configuration is complete and readings are stable
              </span>
            </div>
          </div>
        </Card>
      </div>
    </Modal>
  );
};

export default MeterInstallationModeModal;
